import { useState } from 'react';
import { SVLoader, SVTable } from '../core';
import { SVTableRow } from '@/types/common';
import { ScheduledTransactionStatus } from '@/types/transactions';
import { useGetScheduledTransactionList } from '@/hooks/requests/useTransactions';
import { formatCurrency } from '@/utils/currency';
import { ROUTES } from '@/constants/routes';

type ScheduledTransactionsListProps = {
  status: ScheduledTransactionStatus;
  bankAccountId: string;
};

export default function ScheduledTransactionsList({
  status,
  bankAccountId,
}: ScheduledTransactionsListProps) {
  const [page, setPage] = useState(0);

  const {
    scheduledTransactionsList,
    isScheduledTransactionListPending,
    totalRecordCount,
  } = useGetScheduledTransactionList(bankAccountId, {
    page: 0,
    statuses: [status],
  });

  if (isScheduledTransactionListPending) {
    return <SVLoader />;
  }

  const rows: SVTableRow[] = scheduledTransactionsList.map(transaction => {
    const {
      uuid,
      amount,
      description,
      displayType,
      nextOccurrenceDate,
      toAccount,
      fromAccount,
    } = transaction;

    return {
      key: uuid,
      cells: [
        {
          key: `${uuid}Date`,
          content: nextOccurrenceDate,
          type: 'date',
        },
        {
          key: `${uuid}Amount`,
          content: formatCurrency({ amount: amount.value }),
          type: 'text',
        },
        {
          key: `${uuid}Description`,
          content: description,
          type: 'text',
        },
        {
          key: `${uuid}Type`,
          content: displayType,
          type: 'text',
        },
        {
          key: `${uuid}ToAccount`,
          content: toAccount,
          type: 'text',
        },
        {
          key: `${uuid}FromAccount`,
          content: fromAccount,
          type: 'text',
        },
        {
          key: `${uuid}Details`,
          content: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}${ROUTES.SCHEDULED_TRANSACTIONS}/${uuid}`,
          type: 'link',
        },
      ],
    };
  });

  return (
    <SVTable
      ariaLabel="Scheduled Transaction List"
      headers={[
        {
          key: 'date',
          content: 'Scheduled Date',
        },
        {
          key: 'amount',
          content: 'Amount',
        },
        {
          key: 'description',
          content: 'Description',
        },
        {
          key: 'type',
          content: 'Type',
        },
        {
          key: 'toAccount',
          content: 'To Account',
        },
        {
          key: 'fromAccount',
          content: 'From Account',
        },
        {
          key: 'details',
          content: '',
        },
      ]}
      rows={rows}
      hasPagination
      page={page}
      setPage={setPage}
      totalRecordCount={totalRecordCount}
      emptyTableContent="No scheduled transactions to display"
    />
  );
}
